import { z } from 'zod';
import { belegExtractionSchema, belegPositionSchema, extractionSourceSchema, } from './beleg-auslese.js';
import { incomingTaxRegimeSchema, vatCheckMethodSchema, vatCheckResultSchema, } from './master-data.js';
import { centAmount, isoDate, nonNegativeAmount, uuid } from './posting.js';
import { vendorErkennungArtSchema } from './vendor-erkennung.js';
/**
 * Die Verträge der Eingangsbelege (Spec Eingangsrechnungen 11 und 12).
 *
 * **Kein `number` für einen Betrag, auch nicht im Vertrag.** Ein Vertrag, der
 * `netTotal: z.number()` sagt, macht Hausregel 1 an der Aussengrenze zunichte —
 * und dort fällt es erst auf, wenn ein Cent fehlt. Jeder Betrag läuft deshalb
 * über `centAmount` beziehungsweise `nonNegativeAmount` und verlässt das Schema
 * als `Money`, nicht als `string`.
 *
 * **Warum die Antwortformen hier stehen und nicht nur in `apps/web`.** Sie
 * haben zwei Leser: die Oberfläche und die Endpunktprüfungen aus Aufgabe 40,
 * und die zweite ist die wichtigere. `.strict()` bindet dort die Naht: Legt ein
 * Dienst der Antwort ein Feld bei, das der Vertrag nicht kennt — den
 * **Ablageschlüssel** des Archivs zum Beispiel —, wird die Prüfung rot statt
 * still. Ein `satisfies` sähe das nicht: Ein `.strict()`-Verstoss ist ein
 * Prüfergebnis zur Laufzeit und kein Typfehler.
 *
 * **Der Ablageschlüssel steht in keiner dieser Formen, und das ist Absicht.**
 * `storage_key` ist der Pfad im Archiv des Betreibers. Er ist die Antwort auf
 * eine Frage, die niemand von aussen stellt, und dieselbe Regel wie beim
 * Auszugsupload der Bankanbindung.
 */
// --- Die Aufzählungen aus Migration 0043 ----------------------------------
/**
 * Die drei Empfangswege.
 *
 * **Sie stehen im Vertrag, weil `origin` Pflicht und unveränderlich ist**
 * (`E-265`). Der Weg entscheidet über die **Belegbarkeit** des Eingangsdatums:
 * bei `email` trägt der Empfangsstempel, bei `portal` das Abrufprotokoll, bei
 * `paper` allein die Erklärung des Erfassers. Ein vierter Wert wäre eine
 * Belegbarkeit, über die niemand etwas gesagt hat.
 */
export const incomingOriginSchema = z.enum(['email', 'portal', 'paper']);
/**
 * Die Herkunft eines Archivstücks, wie es im Archiv steht: die drei
 * Empfangswege und `vorsystem` (`0302`, `LX-archivvorsystem`, `E-1876`).
 *
 * **Ein eigener Satz und nicht der der Empfangswege erweitert.** `vorsystem` ist
 * kein Weg, auf dem jemand eine Datei hochlädt: Die Übernahme aus einem
 * Vorsystem legt die Belegdatei einer übernommenen Eingangsrechnung selbst ab,
 * mit dem Belegdatum als Eingangsdatum. Das Hochladen (`incomingUploadRequestSchema`)
 * kennt deshalb weiter nur die drei Wege; gelesen wird dieser Satz — sonst
 * scheiterte die Oberfläche am ersten übernommenen Archivstück.
 */
export const incomingArchivOriginSchema = z.enum(['email', 'portal', 'paper', 'vorsystem']);
/** Was für ein Stück im Archiv liegt. */
export const incomingDocumentKindSchema = z.enum([
    'invoice_file',
    'email_message',
    'paper_scan',
    'structured_part',
]);
/** Die fünf Zustände eines Eingangsbelegs. */
export const incomingInvoiceStatusSchema = z.enum([
    'received',
    'captured',
    'approved',
    'posted',
    'discarded',
]);
/**
 * Der Stand einer aus einem Vorsystem übernommenen Eingangsrechnung in der
 * Schreibweise des Vorsystems -- `incoming_invoice.vorsystem_status`
 * (`0302`, `incoming_invoice_vorsystem_status_form`). Entwurf, offen, bezahlt,
 * aufgehoben: Das ist der **Zahlstand dort** und sagt nichts über eine
 * Erfassung hier.
 */
export const incomingVorsystemStatusSchema = z.enum(['draft', 'open', 'paidoff', 'voided']);
/**
 * Welche Ansicht der Belegliste gemeint ist (`W-eingangsarchivvorsystem`,
 * `E-2083`).
 *
 * - `arbeit` (Vorgabe): die Arbeitsliste des Eingangskorbs -- eigene
 *   Rechnungen und übernommene mit offenem Betrag zum Stichtag (`E-1845`).
 * - `archiv-vorsystem`: die übernommenen Rechnungen, die **nicht** in der
 *   Arbeitsliste stehen. Die Bedingung ist die Verneinung der der Arbeitsliste
 *   und nicht „vor dem Stichtag", damit sie wahr bleibt, wenn eine
 *   Vorsystem-Rechnung ab dem Beginn der Buchführung gebucht wird.
 */
export const incomingInvoiceAnsichtSchema = z.enum(['arbeit', 'archiv-vorsystem']);
/**
 * Wo die Seite der Belegliste beginnt (`W-archivfilterserver`, `E-2110`) --
 * die Hausform `limit`/`offset` der übrigen Listen (`documents`,
 * `portal-vorgaenge`). **Nur die Archivansicht blättert:** Die Arbeitsliste
 * ist ein Arbeitsvorrat und kennt keine zweite Seite; ein `offset` über 0 mit
 * `ansicht=arbeit` ist ein `400` und kein stilles Ignorieren, weil eine
 * übergangene Seite eine vollständige, plausible und falsche Liste gäbe.
 * Ein Query-Wert kommt als Zeichenkette an, daher `coerce`.
 */
export const incomingInvoiceOffsetSchema = z.coerce.number().int().min(0).max(1_000_000);
/** Rechnung oder Eingangsgutschrift. Baulich derselbe Beleg. */
export const incomingDocumentTypeSchema = z.enum(['invoice', 'credit_note']);
/**
 * Woher die **gespeicherten** Werte eines ganzen Belegs stammen —
 * `incoming_invoice.extraction_source` (`0043`, `0045:53`,
 * `structured_xml` seit `0057`).
 *
 * **Nicht dasselbe wie `extractionSourceSchema` aus `beleg-auslese.ts`.** Jene
 * beschreibt, woher ein **ausgelesenes Feld** eines Archivstücks stammt (drei
 * Werte, ohne `manual` und ohne `ocr` — beides kann eine Auslesung per
 * Definition nicht sein). Diese hier ist die Spalte am **Beleg**, EINE je
 * Zeile und nicht je Feld (`capture.service.ts`,
 * `bestimmeExtractionSource()`), und trägt deshalb auch `manual` — der
 * Vorgabewert, sobald auch nur ein einziges ausgelesenes Feld überschrieben
 * wurde — und `ocr`, obwohl Sprosse 3 nicht gebaut ist: Die Spalte lässt den
 * Wert seit `0043` zu, und ein Vertrag, der ihn ausschlösse, wäre schmaler als
 * die Wahrheit, die er beschreibt.
 */
export const incomingExtractionSourceSchema = z.enum([
    'manual',
    'zugferd_xml',
    'structured_xml',
    'text_layer',
    'ocr',
]);
// `incomingTaxRegimeSchema` steht seit `C-vendorplus` in `master-data.ts` --
// `vendor.default_tax_regime` (dort) und `incoming_invoice_tax_line.tax_regime`
// (hier) sind dieselbe Aufzaehlung, und der Import oben ersetzt die zweite
// Abschrift.
/**
 * Warum ein Beleg verworfen wurde.
 *
 * **Verwerfen ist kein Löschen** (`D-12`, `E-262`). Ein verworfener Beleg
 * bleibt sichtbar und aufbewahrungspflichtig; der Grund sagt, was mit ihm los
 * war, und ohne ihn wäre `discarded` eine Behauptung ohne Inhalt.
 */
export const incomingDiscardReasonSchema = z.enum([
    'duplicate',
    'not_an_invoice',
    'received_in_error',
]);
/** Der erwartete Rhythmus eines Kreditors — Grundlage des Lückenberichts. */
export const expectedCadenceSchema = z.enum(['monthly', 'quarterly', 'yearly']);
/**
 * Die drei Formklassen eines Archivstücks — dieselbe Einteilung wie im
 * Messbericht (`formklassenSchema` unten) und in der Erfassungsmaske.
 */
export const formklasseSchema = z.enum(['xml', 'textlage', 'scan']);
/** Was das Hochladen ergeben hat — gespeichert oder schon vorhanden (`E-244`). */
export const uploadOutcomeSchema = z.enum(['stored', 'duplicate']);
// --- Das Archivstück ------------------------------------------------------
/**
 * Das Ergebnis der Formatsonde — **drei Zahlen, zwei Merker, kein Feldwert**
 * (`E-255`).
 *
 * `probeVersion` steht neben jedem Ergebnis (`E-254`): Eine Messung ohne die
 * Fassung des Messgeräts lässt sich später nicht mehr einordnen, und die Leiter
 * aus Chunk 6 entscheidet an genau diesen Zahlen.
 */
export const probeResultSchema = z
    .object({
    probeVersion: z.string(),
    /** Ist ein XML dabei — eingebettet (hybrid) oder als das Stück selbst. */
    hasXml: z.boolean(),
    /** Dateiname und `/AFRelationship` des Anhangs — **nicht sein Inhalt**. */
    xmlHint: z.string().nullable(),
    /** Zahl der entnehmbaren Textzeichen. `0` heisst **echter Scan**. */
    textChars: z.number().int().nonnegative(),
    /** Seitenzahl. `null` bei einem XML — ein XML hat keine Seiten. */
    pageCount: z.number().int().nonnegative().nullable(),
    /**
     * Das Sondenergebnis in **einem Satz**, für die Erfassungsmaske.
     *
     * **Er wird auf dem Server gebildet und nicht in der Maske.** Sonst stünden
     * die drei Klassen zweimal da — einmal im Messbericht aus Chunk 6 und
     * einmal in einer Bedingung im Browser —, und die zweite driftete beim
     * ersten neuen Fall. Drei Werte, dieselben wie die Formklassen des
     * Berichts.
     */
    formklasse: formklasseSchema,
})
    .strict();
/**
 * Ein Archivstück, wie ein Endpunkt es herausgibt.
 *
 * **Ohne `storageKey`.** Siehe der Kopfkommentar dieser Datei.
 */
export const incomingDocumentSchema = z
    .object({
    documentId: uuid,
    vendorId: uuid.nullable(),
    invoiceId: uuid.nullable(),
    kind: incomingDocumentKindSchema,
    origin: incomingArchivOriginSchema,
    sha256: z.string().regex(/^[0-9a-f]{64}$/, 'SHA-256 in Kleinbuchstaben erwartet'),
    byteSize: z.number().int().nonnegative(),
    /** Der Medientyp, **wie empfangen und nicht geraten** (GoBD Rz. 131). */
    mediaType: z.string(),
    originalFilename: z.string().nullable(),
    receivedAt: isoDate,
    uploadedAt: z.string(),
    /** Nachrichtlich gerechnet, ohne Auslöser (`D-12`). */
    retentionUntil: isoDate.nullable(),
    /**
     * Wann die Archivdatei gelöscht wurde — `null`, solange sie liegt
     * (Migration `0145`, Owner-Entscheid vom 2026-09-03).
     *
     * **Am Stück selbst und nicht an der Korbzeile.** Es beantwortet die
     * Frage „trägt dieses Archivstück noch Bytes?", und die stellt jeder
     * Leser: die Vorschau, die Erfassungsmaske, und vor allem die Antwort auf
     * ein **erneutes Hochladen derselben Datei** — die kommt als Mehrstück
     * zurück (`E-244`), und ohne dieses Feld sähe der Anwender dort ein
     * Archivstück, dessen Bytes es nicht mehr gibt, ohne es zu erfahren.
     *
     * Gesetzt wird es ausschliesslich nach einem Verwerfen mit dem Grund
     * `kein_beleg`; die drei anderen Gründe behalten ihre Datei (`E-765`).
     */
    dateiGeloeschtAm: z.string().nullable(),
    probe: probeResultSchema,
})
    .strict();
/**
 * Die Antwort auf das Hochladen.
 *
 * **`duplicate` ist ausdrücklich kein Fehler** (`E-244`). `[G]` UStAE Abschnitt
 * 14c.1 Abs. 4 Satz 7: „Es ist unschädlich, wenn die Datei zu einer E-Rechnung
 * […] mehrfach übersandt wird, solange es sich um dieselbe Rechnung handelt."
 * Die doppelte Zusendung ist kein Fehler des Absenders; sie ist ausschliesslich
 * ein Buchhaltungsproblem beim Empfänger — und der Empfänger löst es, indem er
 * auf das **vorhandene** Stück verweist.
 *
 * **Die begleitende E-Mail ist `null` und nicht abwesend.** Ein Beleg ohne Mail
 * ist der Normalfall auf zwei von drei Empfangswegen, und ein Aufrufer soll die
 * Abwesenheit lesen können, statt sie aus dem Schweigen zu schliessen.
 */
export const incomingUploadResultSchema = z
    .object({
    outcome: uploadOutcomeSchema,
    document: incomingDocumentSchema,
    emailDocument: incomingDocumentSchema.nullable(),
})
    .strict();
// --- Der Beleg ------------------------------------------------------------
/**
 * Eine gespeicherte Position der Eingangsrechnung (`eingangsrechnung_position`,
 * Migration `0325`): die Form der Auslese plus ihre laufende Nummer.
 */
export const incomingPositionSchema = belegPositionSchema
    .extend({ nr: z.number().int().positive() })
    .strict();
/** Eine Steuerzeile — je Zeile und nicht je Beleg (`D-7`). */
export const incomingTaxLineSchema = z
    .object({
    lineNo: z.number().int().positive(),
    taxRegime: incomingTaxRegimeSchema,
    /** Der **Satz**, kein Betrag — und trotzdem nie durch `number`. */
    taxRate: z.string(),
    netAmount: centAmount,
    /** Was auf der Rechnung **steht**, nicht was richtig wäre (`E-251`). */
    statedTaxAmount: nonNegativeAmount,
})
    .strict();
/** Ein Eingangsbeleg in der Liste. */
export const incomingInvoiceSchema = z
    .object({
    invoiceId: uuid,
    vendorId: uuid,
    vendorName: z.string(),
    creditorNo: z.string().nullable(),
    type: incomingDocumentTypeSchema,
    status: incomingInvoiceStatusSchema,
    /**
     * **`null` ist zulässig und strukturell** (`E-245`). § 33 Satz 1 UStDV
     * zählt für eine Rechnung bis 250 € vier Angaben auf, und die
     * Rechnungsnummer ist nicht darunter.
     */
    invoiceNo: z.string().nullable(),
    invoiceDate: isoDate,
    serviceDate: isoDate.nullable(),
    serviceMonth: isoDate.nullable(),
    receivedAt: isoDate,
    currency: z.string().length(3),
    netTotal: centAmount,
    taxTotal: nonNegativeAmount,
    grossTotal: centAmount,
    /**
     * Was an dieser Rechnung **noch zu zahlen ist** (`E-1852`) — nicht der
     * Bruttobetrag. Bei einer aus einem Vorsystem übernommenen Eingangsrechnung
     * ist es ihr offener Betrag zum Stichtag (`vorsystem_offen_stichtag`,
     * `0252`, `D-55`): Was dort vor dem Stichtag bezahlt wurde, steht in keinem
     * Journal dieses Systems, und `grossTotal` nennte eine Schuld, die es nicht
     * mehr gibt. Sonst der Bruttobetrag abzüglich der gebuchten Zahlungen.
     * Dieselbe Rechnung wie `offen` in der Offene-Verbindlichkeiten-Auswertung —
     * sie steht in `apps/api/src/kontierung/eingang-offener-betrag.ts` genau
     * einmal.
     */
    offenerBetrag: centAmount,
    expenseAccountNumber: z.string().nullable(),
    dueDate: isoDate.nullable(),
    /**
     * Freitext, was diese Rechnung betrifft — `null`, solange niemand ihn
     * eingetragen hat. **Additiv, kein Bestandteil der zehn Erfassungsfelder**
     * (`0089_eingangsbeleg_beschreibung.sql`) — dieselbe Bauform wie die
     * additiven Felder in `incomingPrefillSchema` unten ("Die sechs bleiben sechs"):
     * rein organisatorisch, ohne Vorbelegung und ohne Auslese aus dem Beleg.
     */
    beschreibung: z.string().nullable(),
    /** Der § 14c-Verdacht, am **Betrag** gemessen und nicht am Satz. */
    statedTaxSuspect: z.boolean(),
    discardedReason: incomingDiscardReasonSchema.nullable(),
    discardedNote: z.string().nullable(),
    approvedAt: z.string().nullable(),
    /**
     * Die sachliche Freigabe war eine Einzelfreigabe (`D-31`, `0263`): Wer
     * erfasst hat, hat auch freigegeben — nur unter einer Freigaberegel ohne
     * vier Augen. `optional()`, weil ältere Fixturen das Feld nicht führen; der
     * Dienst liefert es an jeder Zeile.
     */
    einzelfreigabe: z.boolean().optional(),
    /**
     * Aus einem Vorsystem übernommen (Lexware Office) — **nur lesbar**: Ein
     * solcher Beleg wird hier weder erfasst noch freigegeben noch gebucht,
     * seine Vorsteuer hat das Vorsystem angemeldet (`E-1844`, `E-1845`). Die
     * Liste führt ihn nur, solange er einen offenen Betrag trägt, und nur
     * ohne Zustandsfilter. `optional()` aus demselben Grund wie
     * `einzelfreigabe`; der Dienst liefert es an jeder Zeile.
     */
    ausVorsystem: z.boolean().optional(),
    /**
     * Der Stand dieser Rechnung im Vorsystem (`incomingVorsystemStatusSchema`),
     * `null` an einer hier erfassten. Die Archivansicht des Eingangskorbs zeigt
     * ihn als „Zahlstand aus dem Vorsystem" (`E-2083`). `optional()` aus
     * demselben Grund wie `ausVorsystem`; der Dienst liefert es an jeder Zeile.
     */
    vorsystemStatus: incomingVorsystemStatusSchema.nullable().optional(),
    /** Wie viele Archivstücke an diesem Beleg hängen (`D-3`, n:1). */
    documentCount: z.number().int().nonnegative(),
    /** Offene Dublettenkandidaten dieses Belegs. */
    openDuplicateCount: z.number().int().nonnegative(),
})
    .strict();
/**
 * Die Liste — und **sie sagt, was sie zählt**.
 *
 * `total` ist die Zahl der Belege **nach** den gesetzten Filtern und nicht der
 * Bestand. Eine Zahl neben einem Vorfilter, die das verschweigt, führt einen
 * aufmerksamen Leser in die Irre (Handbuch §3.132): „412 Belege" neben einem
 * Zustandsfilter liest sich als Gesamtzahl und ist keine. `gesamtOhneFilter`
 * steht deshalb daneben.
 *
 * `mitHinweis` — seit `B-korbhinweiszahl` (2026-09-01, `E-581`) — zählt über
 * DENSELBEN Filterbereich wie `total`, wie viele Belege einen offenen
 * Dublettenkandidaten (`openDuplicateCount > 0`) ODER einen § 14c-Verdacht
 * (`statedTaxSuspect`) tragen — **nicht** über die zurückgegebenen Zeilen, die
 * `limit` kappt. Ohne dieses Feld liesse sich die Zahl nur aus `invoices`
 * bilden, und die ist auf die Seite begrenzt.
 */
export const incomingInvoiceListSchema = z
    .object({
    invoices: z.array(incomingInvoiceSchema),
    total: z.number().int().nonnegative(),
    gesamtOhneFilter: z.number().int().nonnegative(),
    mitHinweis: z.number().int().nonnegative(),
})
    .strict();
/** Was beim Erfassen herauskommt — samt der Kandidaten, die dabei aufgefallen sind. */
export const incomingCaptureResultSchema = z
    .object({
    invoiceId: uuid,
    status: incomingInvoiceStatusSchema,
    currency: z.string().length(3),
    netTotal: centAmount,
    taxTotal: nonNegativeAmount,
    grossTotal: centAmount,
    /** Die Kennungen der **weichen** Kandidaten, die diese Erfassung ausgelöst hat. */
    kandidaten: z.array(uuid),
    statedTaxSuspect: z.boolean(),
    /**
     * Die zwei Messwerte, **gerechnet und nicht gemeldet** (Spec 8.2).
     *
     * `captureSeconds` ist `null`, wenn die Maske ihren Öffnungszeitpunkt nicht
     * mitgeschickt hat — der Weg des Durchgangs über die Kommandozeile. Eine
     * erfundene Zahl verfälschte dort den Median.
     */
    captureSeconds: z.number().int().nonnegative().nullable(),
    prefillKept: z.number().int().min(0).max(6),
})
    .strict();
/**
 * Ein erfasster Beleg **vollständig** — `GET /incoming-invoices/:id`.
 *
 * **Anders als `incomingInvoiceSchema`, die Zeile der Liste.** Die Liste
 * nennt, was eine Übersicht braucht; sie hat weder die Steuerzeilen noch die
 * Herkunft noch den Verweis auf das Archivstück, weil eine Zeile unter zwanzig
 * anderen davon nichts zeigen könnte. Wer einen einzelnen Beleg wieder öffnet,
 * braucht **alle** zehn Maskenfelder zurück — sonst liesse sich die
 * Erfassungsmaske aus dieser Antwort nicht befüllen.
 *
 * **`taxLines` statt `taxRegime`/`taxRate` am Kopf.** Der Beleg trägt seine
 * Steuersätze über `incoming_invoice_tax_line` (`D-7`), je Zeile und nicht je
 * Beleg — derselbe Grund wie beim Erfassen: Eine Rechnung kann gemischt sein.
 *
 * **`extractionSource` ist die Spalte, nicht neu bestimmt.** Sie beantwortet
 * „woher kamen die gespeicherten Werte" (`capture.service.ts`,
 * `bestimmeExtractionSource()`) — eine zweite Rechnung im Endpunkt wäre eine
 * zweite Antwort auf dieselbe Frage.
 *
 * **`documentIds` und nicht `documentId`.** Ein Beleg kann an mehreren
 * Archivstücken hängen (`D-3`, n:1) — eine einzelne Kennung behauptete eine
 * 1:1-Beziehung, die es nicht gibt.
 */
export const incomingInvoiceDetailSchema = z
    .object({
    invoiceId: uuid,
    vendorId: uuid,
    vendorName: z.string(),
    creditorNo: z.string().nullable(),
    type: incomingDocumentTypeSchema,
    status: incomingInvoiceStatusSchema,
    invoiceNo: z.string().nullable(),
    invoiceDate: isoDate,
    serviceDate: isoDate.nullable(),
    serviceMonth: isoDate.nullable(),
    receivedAt: isoDate,
    currency: z.string().length(3),
    netTotal: centAmount,
    taxTotal: nonNegativeAmount,
    grossTotal: centAmount,
    /**
     * Was an dieser Rechnung noch zu zahlen ist (`E-1852`) — wie in der Liste,
     * mit dem einen Unterschied: `null` an einer im Vorsystem erledigten
     * Eingangsrechnung, die hier trotzdem lesbar ist und nichts mehr schuldet.
     */
    offenerBetrag: centAmount.nullable(),
    taxLines: z.array(incomingTaxLineSchema),
    /** Die Rechnungszeilen, wenn welche erfasst sind — leer, wenn keine (`E-2021`). */
    positionen: z.array(incomingPositionSchema),
    expenseAccountNumber: z.string().nullable(),
    dueDate: isoDate.nullable(),
    /** Freitext, was diese Rechnung betrifft — additiv, siehe `incomingInvoiceSchema` oben. */
    beschreibung: z.string().nullable(),
    statedTaxSuspect: z.boolean(),
    /** Woher die gespeicherten Werte stammen — die Spalte am Beleg. */
    extractionSource: incomingExtractionSourceSchema,
    discardedReason: incomingDiscardReasonSchema.nullable(),
    discardedNote: z.string().nullable(),
    approvedAt: z.string().nullable(),
    /** Die sachliche Freigabe war eine Einzelfreigabe (`D-31`), siehe `incomingInvoiceSchema`. */
    einzelfreigabe: z.boolean().optional(),
    /**
     * Wer die zehn Felder erfasst hat — die zweite Hälfte des
     * Vier-Augen-Prinzips (`D-22`, 2026-08-23).
     *
     * Steht im Vertrag, damit die Maske den Freigabeknopf **erklären** kann,
     * statt ihn anzubieten und in die 409 zu laufen: Wer erfasst hat, gibt
     * nicht frei (`incoming_invoice_four_eyes`).
     */
    capturedBy: uuid.nullable(),
    /** Aus einem Vorsystem übernommen — nur lesbar, siehe `incomingInvoiceSchema`. */
    ausVorsystem: z.boolean().optional(),
    /** Die Archivstücke, die an diesem Beleg hängen (`D-3`, n:1). */
    documentIds: z.array(uuid),
    /**
     * Die begleitende E-Mail dieses Belegs — **`null`, wenn keine da ist**
     * (`D-11`, `AN-6`, bestätigt vom Auftraggeber am 2026-08-23 als `EO-15`).
     *
     * **Sie steht zusätzlich in `documentIds`, und das ist kein Widerspruch.**
     * `documentIds` sagt, **welche** Stücke an diesem Beleg hängen; dieses Feld
     * sagt, **welches davon die Mail ist**. Ohne die zweite Auskunft müsste
     * jeder Leser alle Stücke einzeln nachschlagen, nur um zu erfahren, welches
     * er nicht als Rechnung anzeigen darf — und die Maske täte es dann falsch,
     * indem sie eine `.eml` in die Belegvorschau legte.
     *
     * **Höchstens eine.** Die Ablage trägt baulich mehrere (`D-3`, n:1); die
     * Erfassung hängt genau **eine** an, weil die Entscheidung von „der
     * begleitenden E-Mail" spricht. Liegen historisch mehrere an einem Beleg —
     * über den Ingest-Weg möglich —, nennt dieses Feld die **zuerst
     * hochgeladene**.
     */
    mailDocumentId: uuid.nullable(),
})
    .strict();
// --- Die Vorbelegung ------------------------------------------------------
/**
 * Der Stand der USt-IdNr.-Prüfung — **kein Feld, sondern eine Angabe daneben**.
 *
 * Sie steht in der Maske, ohne ein Formularfeld zu sein: Der Erfasser soll sie
 * sehen und nicht eintippen können.
 */
export const vatStatusSchema = z
    .object({
    checkedAt: z.string().nullable(),
    result: vatCheckResultSchema.nullable(),
    method: vatCheckMethodSchema.nullable(),
    matchesCurrentVatId: z.boolean(),
})
    .strict();
/**
 * Woran ein Kreditorvorschlag hängt — **an einer Kennung des Belegs, nie an einer
 * Ähnlichkeit**.
 *
 * `vat_id` und `iban` sind die zwei Kennungen des Kreditorenstamms und stehen
 * kanonisiert vergleichbar in der Datei: Die USt-IdNr. trägt in `vendor` einen
 * **eindeutigen** Index (`0014:199`), die IBAN nicht — zwei Kreditoren
 * desselben Konzerns dürfen dieselbe Bankverbindung führen. `rule` ist jede
 * weitere Kennung, die ein Mensch (oder, bei den starken Arten, die Erfassung
 * eines Belegs) für einen Kreditor festgehalten hat: Gläubiger-ID,
 * Kundennummer, Domain, Name, Textstelle. Welche, sagt `quelle`. Ein Vorschlag
 * nach **Ähnlichkeit** steht hier bewusst nicht: Eine Ähnlichkeit schriebe den
 * falschen Kreditor in eine Buchung, ohne dass es jemandem auffiele — eine Regel
 * ist eine Entscheidung, eine Ähnlichkeit ein Mass.
 */
export const vendorSuggestionMatchSchema = z.enum(['vat_id', 'iban', 'rule']);
/**
 * Woher die Kennung stammt, an der ein Vorschlag hängt — `stamm` (IBAN oder
 * USt-IdNr. im Kreditorenstamm) oder `regel:<art>` (eine Erkennungsregel des
 * Kreditors, Nachtrag vom 2026-10-16, `D-23`).
 *
 * **Eine Zeichenkette mit fester Form und kein Objekt**, weil sie in der Maske
 * als Merkzeichen steht und in einer Liste als Tooltip: ein Wert, der an einer
 * Stelle gelesen und nirgends verglichen wird. Die Form hält ein Muster; die
 * Arten kommen aus `vendorErkennungArtSchema`, nicht aus einer Abschrift.
 */
export const vendorSuggestionQuelleSchema = z
    .string()
    .regex(new RegExp(`^(?:stamm|regel:(?:${vendorErkennungArtSchema.options.join('|')}))$`), 'Quelle muss stamm oder regel:<art> sein');
/**
 * Wie verlässlich der Vorschlag ist. **`stark`** (IBAN, USt-IdNr., Gläubiger-ID)
 * darf die Maske vorbelegen; **`schwach`** (Kundennummer, Domain, Name,
 * Textstelle) legt sie nur nahe — der Erfasser bestätigt sie mit einem Klick.
 */
export const vendorSuggestionStaerkeSchema = z.enum(['stark', 'schwach']);
/**
 * Der vorgeschlagene Kreditor — **genau einer oder gar keiner**.
 *
 * `null` heisst „kein Treffer **oder** mehr als einer". Beides führt zu
 * demselben Verhalten, und das ist Absicht: Wer bei zwei Treffern den ersten
 * nähme, träfe eine Entscheidung, die er nicht treffen kann, und die Maske
 * zeigte sie als Vorschlag an — also als etwas, das jemand geprüft hat.
 */
export const vendorSuggestionSchema = z
    .object({
    vendorId: uuid,
    name: z.string(),
    matchedBy: vendorSuggestionMatchSchema,
    quelle: vendorSuggestionQuelleSchema,
    staerke: vendorSuggestionStaerkeSchema,
})
    .strict();
/**
 * Was der **Beleg** über seinen Aussteller sagt — unabhängig davon, ob ein
 * Kreditor dazu geführt wird.
 *
 * **Wozu der Block da ist.** Findet der Vorschlag oben keinen Kreditor, ist die
 * nächste Frage nicht „welchen nehme ich", sondern „lege ich einen an" — und
 * die Angaben dafür stehen bereits gelesen da. Sie hier auszugeben kostet
 * nichts und erspart, den Beleg ein zweites Mal aufzuschlagen.
 *
 * **`name` kommt ausschliesslich aus dem Rechnungs-XML** (Sprosse 1) — CII
 * `SellerTradeParty/Name`, UBL `AccountingSupplierParty/Party/PartyName/Name`.
 * Aus der **Textlage** wird er nicht gelesen: Ein Firmenname hat dort kein
 * Etikett, an dem er hinge, und was ohne Etikett gefunden wird, ist geraten.
 * Ein geratener Kreditorname in einer Buchhaltung ist teurer als ein leeres
 * Feld — dort bleibt er `null`.
 *
 * **`matched` sagt, ob der Vorschlag oben getroffen hat**, und ist damit die
 * eine Angabe, die ein Verbraucher braucht, ohne zwei Felder gegeneinander
 * prüfen zu müssen.
 */
export const vendorIdentitySchema = z
    .object({
    vatId: z.string().nullable(),
    iban: z.string().nullable(),
    name: z.string().nullable(),
    matched: z.boolean(),
})
    .strict();
/**
 * Ein Kreditor innerhalb eines {@link vendorConflictSchema} — Kennung, Name und
 * die Kennung des Belegs, an der er hängt.
 */
export const vendorConflictPartySchema = z
    .object({
    id: uuid,
    name: z.string(),
    matchedBy: vendorSuggestionMatchSchema,
    quelle: vendorSuggestionQuelleSchema,
})
    .strict();
/**
 * Zwei **starke** Kennungen des Belegs treffen je genau einen aktiven
 * Kreditor — aber auf **verschiedene** — Nachtrag vom 2026-08-24, seit dem
 * 2026-10-16 (`D-23`) über beliebig viele Kandidaten.
 *
 * **Warum das ein eigener Block ist und keine weitere `matchedBy`-Ausprägung.**
 * `ladeKreditorvorschlag()` (`prefill.ts`) prüfte bis hierher die USt-IdNr.
 * und brach beim ersten eindeutigen Treffer ab — eine widersprechende IBAN kam
 * dann nie zur Sprache, und die Maske übernahm den USt-IdNr.-Treffer, als wäre
 * er unbestritten. Das ist die falsche Stille: Zwei Kennungen desselben Belegs,
 * die auf zwei verschiedene Kreditoren zeigen, sind kein Vorschlag, sondern ein
 * **Fund**, den der Erfasser sehen muss, bevor er einen von beiden bucht.
 *
 * **`kandidaten` löst `vatVendor`/`ibanVendor` ab.** Mit den Erkennungsregeln
 * können drei Kennungen (USt-IdNr., IBAN, Gläubiger-ID) auf bis zu drei
 * verschiedene Kreditoren zeigen; zwei feste Felder bildeten das nicht ab.
 * Jeder Kandidat nennt die Kennung, an der er hängt.
 *
 * Bei einem Konflikt bleibt `vendorSuggestion` **`null`** — es gibt keinen
 * geprüften Vorschlag mehr, den die Maske stillschweigend übernehmen dürfte —
 * und dieser Block trägt stattdessen alle Kandidaten benannt.
 */
export const vendorConflictSchema = z
    .object({
    kandidaten: z.array(vendorConflictPartySchema).min(2).readonly(),
})
    .strict();
// --- Der Eingangskorb: Vorschlag und Verwerfen (Migration 0136) -----------
//
// Zwei Owner-Wünsche vom 2026-09-02, Auftrag `F-korbspalten`. Beide Formen
// stehen **hier unten** und nicht oben bei `incomingDocumentSchema`, weil sie
// `vendorSuggestionSchema` brauchen — und ein `const` vorwärts zu lesen wäre
// ein Laufzeitfehler beim Laden des Moduls, kein Typfehler.
/**
 * Ein Feld, das die Ausleseleiter beim **Hochladen** gelesen hat — Wert und
 * Quelle.
 *
 * **Eigene Form und nicht `belegExtractionSchema`.** Jene trägt zwölf Felder
 * für die Erfassungsmaske; der Korb zeigt **zwei** davon, und ein Vertrag, der
 * zwölf verspricht und zwei füllt, wäre eine Einladung, die anderen zehn
 * nachzureichen, ohne dass jemand die Ablage dafür gebaut hat.
 */
function korbfeld(wert) {
    return z.object({ wert, quelle: extractionSourceSchema }).strict();
}
/**
 * Was die Ausleseleiter beim Hochladen gelesen hat — **ein Vorschlag, kein
 * Belegwert**.
 *
 * **Der Owner nennt es „der **mögliche** Kreditor", und das Wort trägt die
 * ganze Form.** Was hier steht, hat kein Mensch bestätigt: Es ist das, was in
 * der Datei stand, gelesen von Sprosse 1 (eingebettetes XML) und Sprosse 2
 * (Textlage des PDF, **kein OCR**). Der erfasste Wert entsteht erst in der
 * Maske, und bis dahin ist jede Angabe hier eine Vermutung, die die Erfassung
 * bestätigt oder verwirft.
 *
 * **`vendorSuggestion` wird beim LESEN aufgelöst und ist nicht abgelegt.**
 * Abgelegt sind die **Kennungen** des Belegs (`vendorIdentity`); der Kreditor
 * dazu wird bei jeder Abfrage neu gesucht. Grund: Ein beim Hochladen
 * eingefrorener Kreditorverweis bliebe falsch, wenn der Kreditor erst danach
 * angelegt wird — und genau das ist in dieser Ära der Normalfall
 * (`prefill.ts`, Nachtrag vom 2026-08-23).
 *
 * **`vendorConflict` ist ein `boolean` und nicht der volle Block.** Die
 * Kreditorliste des Korbs braucht die Auskunft „hier zeigen die zwei
 * Kennungen auf verschiedene Kreditoren, ich schlage deshalb keinen vor" —
 * **wer** die beiden sind, beantwortet die Erfassungsmaske, die den vollen
 * `vendorConflictSchema`-Block aus der Vorbelegung ohnehin bekommt. Eine
 * Tabellenspalte, die zwei Kreditornamen zeigt, ist keine Spalte mehr.
 */
export const incomingKorbvorschlagSchema = z
    .object({
    /** Die Fassung des Lesers (`E-254`-Bauform, wie `probeVersion`). */
    ausleseVersion: z.string(),
    /** `BT-1`. `null` heisst **nicht gefunden** und nicht „unsicher". */
    invoiceNo: korbfeld(z.string().min(1).max(200)).nullable(),
    /** `BT-112`, der Bruttobetrag — als `Money`, nie durch `number`. */
    grossTotal: korbfeld(nonNegativeAmount).nullable(),
    /** `BT-5`, dreistellig nach ISO 4217. Steht neben dem Betrag. */
    currency: z.string().nullable(),
    /** Genau einer oder gar keiner — dieselbe Regel wie in der Vorbelegung. */
    vendorSuggestion: vendorSuggestionSchema.nullable(),
    /** Was der **Beleg** über seinen Aussteller sagt. */
    vendorIdentity: vendorIdentitySchema,
    /** Die zwei Kennungen treffen **verschiedene** aktive Kreditoren. */
    vendorConflict: z.boolean(),
})
    .strict();
/**
 * Warum ein hochgeladenes Stück verworfen wurde — **eine geschlossene Liste,
 * vom Auftraggeber gesetzt** (2026-09-02).
 *
 * **Deutsche Werte, wo `incomingDiscardReasonSchema` englische führt.** Es
 * sind zwei Aufzählungen über zwei verschiedene Gegenstände, und die Wortlisten
 * decken sich nicht: `received_in_error` am **Beleg** heisst „diese Rechnung
 * geht uns nichts an", `falsche_datei` hier heisst „hier ist etwas anderes
 * hochgeladen worden als gemeint". Übersetzt wird nichts, damit die Zeile in
 * der Datenbank dasselbe Wort trägt wie der Knopf, der sie erzeugt.
 */
export const incomingDocumentDiscardReasonSchema = z.enum([
    'falsche_datei',
    'dublette',
    'kein_beleg',
    'sonstiges',
]);
/**
 * Der Verwerfensvermerk an einem Archivstück — **`null`, solange keiner
 * vorliegt**.
 *
 * **Verworfen heisst nicht: die Zeile ist gelöscht** (`E-262`). Der SHA-256
 * und die Zeile in `incoming_document` bleiben unangetastet; was entsteht, ist
 * eine **zusätzliche** Zeile mit Grund, Person und Zeitpunkt.
 * `[G]` § 147 Abs. 1 AO macht einen empfangenen Beleg aufbewahrungspflichtig,
 * ohne danach zu fragen, ob er gebraucht wurde; GoBD Rz. 111 verlangt, dass die
 * Veränderung samt ihres ursprünglichen Zustands erkennbar bleibt — deshalb
 * ist der Grund Pflicht und der Vorgang nicht zurücknehmbar.
 *
 * **Die DATEI bleibt bei drei der vier Gründe.** Beim Grund `kein_beleg` — dem
 * Fehlupload, der weder Buchungsbeleg noch Geschäftsbrief ist — wird sie seit
 * dem Owner-Entscheid vom 2026-09-03 gelöscht (`E-765`, Migration `0145`). Ob
 * das geschehen ist, steht **nicht hier**, sondern am Stück selbst
 * (`dateiGeloeschtAm`): Dieser Vermerk sagt, was ein Mensch entschieden hat,
 * nicht was daraufhin mit den Bytes geschehen ist.
 */
export const incomingDocumentDiscardSchema = z
    .object({
    grund: incomingDocumentDiscardReasonSchema,
    notiz: z.string().nullable(),
    /** Wann verworfen wurde. Der Zeitstempel der **Datenbankuhr**. */
    verworfenAm: z.string(),
})
    .strict();
/**
 * Eine Zeile des Eingangskorbs: das Archivstück **plus** Vorschlag und
 * Verwerfensvermerk.
 *
 * **Eine eigene Form und keine Erweiterung von `incomingDocumentSchema`.**
 * Beide Zusätze sind Auskünfte der **Liste**: Der Vorschlag speist die drei
 * Spalten, die der Owner sehen will, der Vermerk entscheidet, in welchem
 * Reiter die Zeile steht. Das Einzelstück (`GET /incoming-documents/{id}`) und
 * die Antwort auf das Hochladen bleiben unverändert — sie beantworten andere
 * Fragen, und ein Feld, das dort niemand liest, wäre dort Ballast.
 *
 * **`vorschlag: null` ist eine eigene Lesart und nicht „nichts gefunden".** Ein
 * Stück, das vor Migration `0136` hochgeladen wurde, hat keine Auslesezeile —
 * es ist **nicht ausgelesen worden**, was etwas anderes ist als „ausgelesen
 * und leer". Die Oberfläche sagt beides verschieden; ein Strich für beides
 * wäre die Leerstelle ohne Erklärung, die diese Ära vermeidet.
 */
export const incomingKorbzeileSchema = incomingDocumentSchema
    .extend({
    vorschlag: incomingKorbvorschlagSchema.nullable(),
    verworfen: incomingDocumentDiscardSchema.nullable(),
})
    .strict();
/**
 * Die Liste des Eingangskorbs — und **sie sagt, was sie zählt** (Handbuch
 * §3.132).
 *
 * `total` ist die Zahl **nach** den gesetzten Filtern, `gesamtOhneFilter` der
 * Bestand des Archivs.
 */
export const incomingKorbListeSchema = z
    .object({
    documents: z.array(incomingKorbzeileSchema),
    total: z.number().int().nonnegative(),
    gesamtOhneFilter: z.number().int().nonnegative(),
})
    .strict();
/**
 * Der Rumpf von `POST /incoming-documents/{id}/discard`.
 *
 * **Die Notiz ist bei `sonstiges` Pflicht — geprüft im Dienst, nicht hier.**
 * Ein Grund, der „sonstiges" heisst und nichts weiter sagt, ist kein Grund;
 * dieselbe Haltung wie beim Freigeben eines Dublettenkandidaten, wo der Grund
 * ebenfalls Pflicht ist. Durchgesetzt wird es zweifach — von
 * `VerwerfenService.verwirf` (freundliche Antwort, sagt **welches** Feld
 * fehlt) und von `incoming_discard_sonstiges_braucht_notiz` (`0136`, greift
 * auf jedem Schreibweg). Ein `.refine()` **hier** wäre die dritte Fassung
 * derselben Regel und machte den Vertrag zu einem `ZodEffects`, das der
 * OpenAPI-Erzeuger nicht mehr auflösen kann.
 */
export const incomingDocumentDiscardRequestSchema = z
    .object({
    reason: incomingDocumentDiscardReasonSchema,
    note: z.string().trim().min(1).max(2000).nullish(),
})
    .strict();
/**
 * Die Vorbelegung je Kreditor — **sechs Felder, und sie ist sichtbar als
 * solche**.
 *
 * **Warum `felder` und `geoeffnetAm` im Vertrag stehen.** Ohne beide misst
 * `prefill_kept` nichts: `felder` sagt, wie viele Vorschläge es überhaupt gab —
 * „zwei von zwei stehengeblieben" ist sonst von „vier von vier" nicht zu
 * unterscheiden —, und `geoeffnetAm` ist der Anfangspunkt von
 * `capture_seconds`, **von der Serveruhr**. Eine vom Aufrufer gemeldete Dauer
 * wäre eine Behauptung: Sie misst dessen Uhr, dessen Hintergrundtab und dessen
 * Wohlwollen, und die Zahl entscheidet über die Sprossen 2 und 3 der Leiter.
 *
 * **`auslese` kam am 2026-08-22 dazu, bewusst als NEUES Feld und nicht als
 * stille Erweiterung eines bestehenden.** Der Vertrag
 * ist `.strict()`, und jeder bestehende Verbraucher, der ihn liest, tut das
 * weiterhin richtig: Die sechs Felder oben tragen bereits die Vorrangregel
 * „Auslese vor Erfahrungswert" (`auslese.ts`, `vereinige()`) — `currency`,
 * `taxRate` und `amounts` sind der **verschmolzene** Wert, nicht mehr nur der
 * Kreditor-Erfahrungswert. `auslese` daneben ist das **volle** Ausleseergebnis
 * des verknüpften Archivstücks (alle zwölf Felder samt `widersprueche`) — die
 * Oberfläche braucht es, um je Feld die Quelle zu zeigen, und der Speicherweg
 * (`capture.service.ts`), um `extraction_source` zu bestimmen. Ohne
 * Archivstück, oder wenn keins gelesen werden konnte: `LEERE_AUSLESE`.
 *
 * **`dueDate` kam am selben Tag aus demselben Grund dazu, ebenso als NEUES
 * Feld.** `dueDays` bleibt unverändert die rohe Kreditor-Angabe in Tagen —
 * kein Datum, und ohne eigenen Bezug zu einer Rechnung. `dueDate` ist der
 * daraus **gerechnete Vorschlag**: ein aus dem Beleg gelesenes
 * Fälligkeitsdatum, wenn eins dasteht, sonst Rechnungsdatum plus `dueDays`,
 * sonst `null`. Vor diesem Feld zählte `felder` `dueDays !== null` als eine
 * der sechs Vorbelegungen, obwohl kein Formularfeld je einen Vorschlag aus
 * ihr zeigte — `prefill_kept` erreichte dadurch **strukturell** höchstens
 * fünf von sechs, sobald die Auslese kein Fälligkeitsdatum lieferte. `dueDate`
 * ist der Wert, den die Maske tatsächlich anzeigen und der Erfasser tatsächlich
 * stehen lassen kann.
 *
 * **`vendorSuggestion` und `vendorIdentity` kamen am 2026-08-23 dazu, wieder
 * als NEUE Felder.** Der Anlass ist gemessen und nicht gedacht: Die erste
 * echte Eingangsrechnung eines neuen Lieferanten ging leer auf, obwohl die
 * Textlage Rechnungsnummer, Datum, Währung, Netto, Steuer, Brutto, Fälligkeit
 * und IBAN hergab — die Vorbelegung wurde erst **nach** der Wahl eines
 * Kreditors geholt, und bei einem neuen Lieferanten gibt es keinen zu wählen.
 * Seitdem ist `vendorId` am Endpunkt **freiwillig**: Ohne Kreditor stehen die
 * Auslesefelder wie gehabt, die Erfahrungswerte des Kreditors bleiben leer,
 * und die zwei Blöcke hier sagen, **wer** den Beleg ausgestellt hat.
 *
 * **Die sechs bleiben sechs.** Keiner der zwei Blöcke ist eine siebte
 * Vorbelegung: `felder` zählt sie nicht, `incoming_invoice_prefill_range`
 * bleibt `0..6`, und `prefill_kept` misst weiter dieselbe Sache. Ein
 * vorgeschlagener Kreditor ist die **Voraussetzung** einer Vorbelegung und
 * nicht selbst eine.
 *
 * **`vendorConflict` kam am 2026-08-24 dazu, additiv als DRITTES Feld neben
 * `vendorSuggestion` und `vendorIdentity`.** Bis hierher prüfte
 * `ladeKreditorvorschlag()` die USt-IdNr. des Belegs und die IBAN in dieser
 * Reihenfolge und gab beim ersten eindeutigen Treffer zurück — eine
 * widersprechende IBAN kam nie zur Sprache. Zeigen beide Kennungen auf
 * **verschiedene** aktive Kreditoren, ist das kein Vorschlag mehr, den die
 * Maske stillschweigend übernehmen dürfte: `vendorSuggestion` bleibt dann
 * `null`, wie bei keinem oder mehr als einem Treffer auch, und
 * `vendorConflict` trägt zusätzlich, **welche zwei** Kreditoren sich
 * widersprechen. `vendorConflict` ist selbst keine der sechs Vorbelegungen und
 * zählt nicht in `felder`.
 */
/**
 * Woher ein **vorbelegtes Datum** der Erfassungsmaske kommt — Auftrag
 * `ER-auslesedefaults` (2026-09-16).
 *
 * **Eine eigene Aufzählung und ausdrücklich nicht `extractionSourceSchema`.**
 * Jene trägt die Namen der Datenbankspalte `incoming_extraction_source`
 * (`0043`/`0057`) und beantwortet „welche Hälfte der Rechnung hat diesen Wert
 * getragen". Diese hier beantwortet etwas anderes: „hat den Wert der **Beleg**
 * geliefert, der **Kreditor** oder die **Annahme dieses Systems**". Ein
 * `fallback` in jener Aufzählung wäre eine Herkunftsangabe, die es in der
 * Spalte nicht gibt und nie geben darf — ein angenommenes Datum steht auf
 * keinem Beleg.
 *
 * **Und der Unterschied ist der ganze Zweck.** Ein vorbelegtes Feld, dem man
 * nicht ansieht, ob es abgelesen oder angenommen ist, wird entweder blind
 * bestätigt oder jedes Mal neu geprüft; beides ist teuer. Die Maske zeigt
 * deshalb bei `fallback` den Satz aus `hinweis` unter dem Feld.
 */
export const prefillDateSourceSchema = z.enum(['extraction', 'vendor_default', 'fallback']);
/**
 * Ein vorbelegtes Datum: der Wert, seine Herkunft und — beim `fallback` — der
 * Satz, der die Annahme benennt.
 *
 * `hinweis` ist `null`, wo nichts angenommen wurde: Ein abgelesener Wert
 * braucht keine Erklärung, und ein Hinweis an einem Feld, das den Beleg
 * wiedergibt, stumpft die Hinweise ab, die etwas sagen.
 */
export const prefillDateSchema = z
    .object({ wert: isoDate, quelle: prefillDateSourceSchema, hinweis: z.string().nullable() })
    .strict();
export const incomingPrefillSchema = z
    .object({
    taxRegime: incomingTaxRegimeSchema.nullable(),
    expenseAccount: z
        .object({ number: z.string(), validAt: z.boolean() })
        .strict()
        .nullable(),
    currency: z.string().length(3),
    taxRate: z.string().nullable(),
    dueDays: z.number().int().nullable(),
    /**
     * Der gerechnete Fälligkeits-Vorschlag — Auslese vor Erfahrungswert vor
     * **Fallback** (`ER-auslesedefaults`, 2026-09-16: Rechnungsdatum + 30
     * Tage, wenn weder der Beleg noch der Kreditor etwas hergibt).
     */
    dueDate: isoDate.nullable(),
    /**
     * Woher `dueDate` kommt — `null`, solange es selbst `null` ist.
     *
     * **Warum die Herkunft NEBEN dem Wert steht und ihn nicht umhüllt, anders
     * als bei den zwei Feldern darunter.** `dueDate` steht seit dem
     * 2026-08-22 als blanke Zeichenkette im Vertrag und wird von der
     * Oberfläche und von `ersterfassung.ts` gelesen; es in ein Objekt zu
     * verwandeln nähme jedem bestehenden Aufrufer sein Feld weg. Die zwei
     * darunter sind **neu** und haben deshalb keinen solchen Grund — sie
     * tragen Wert und Herkunft in einem Stück, wie es ohne Altlast richtig
     * ist. Die Asymmetrie ist die Altlast und nicht die Absicht.
     */
    dueDateQuelle: prefillDateSourceSchema.nullable(),
    /** Der Satz zu `dueDate`, sobald es angenommen ist — sonst `null`. */
    dueDateHinweis: z.string().nullable(),
    /**
     * Der Leistungstag-Vorschlag — Auslese, sonst das **Rechnungsdatum**.
     *
     * **Der Fallback ist der Wunsch des Auftraggebers vom 2026-09-16, und er
     * ist eine Annahme, die dasteht statt sich zu verstecken.** „Wenn es nicht
     * aus dem Beleg erfasst werden kann, Rechnungsdatum" — bei einer
     * Eingangsrechnung ohne Leistungsangabe ist das die Lesart, die
     * § 31 Abs. 4 UStDV im Regelfall trägt; falsch ist sie dort, wo die
     * Leistung erkennbar in einem anderen Monat lag, und genau dafür steht
     * `quelle: 'fallback'` samt `hinweis` am Feld. Ein leeres Feld wäre die
     * Alternative, und es wird 250-mal im Monat von Hand gefüllt.
     */
    serviceDate: prefillDateSchema.nullable(),
    /**
     * Der Leistungsmonat-Vorschlag — Auslese, sonst der **Monat des
     * Leistungstags** (und damit ohne beides der Monat des Rechnungsdatums),
     * als Monatserster.
     *
     * **Dass hier ein Monat NEBEN einem Tag stehen kann, ist der Unterschied
     * zwischen einem Vorschlag und einem Belegwert.** Sprosse 2 füllt
     * `serviceMonth` ausdrücklich nicht neben einem gelesenen `serviceDate`
     * (`auslese-textlage.ts`): Was aus dem Beleg kommt, darf nicht mehr
     * behaupten, als dort steht. Ein **Vorschlag** darf beides zeigen — der
     * Erfasser sieht die Herkunft und leert, was nicht passt.
     */
    serviceMonth: prefillDateSchema.nullable(),
    amounts: z
        .object({ net: centAmount, tax: nonNegativeAmount, gross: centAmount })
        .strict()
        .nullable(),
    vatStatus: vatStatusSchema,
    /** Wie viele der sechs überhaupt einen Vorschlag tragen. */
    felder: z.number().int().min(0).max(6),
    /** Der Öffnungszeitpunkt der Maske, **von der Serveruhr**. */
    geoeffnetAm: z.string(),
    /** Das volle Ausleseergebnis des verknüpften Archivstücks. */
    auslese: belegExtractionSchema,
    /**
     * Der aus dem Beleg erschlossene Kreditor — `null`, sobald ein Kreditor
     * feststeht (dann ist nichts vorzuschlagen), und `null` bei keinem oder
     * mehr als einem Treffer.
     */
    vendorSuggestion: vendorSuggestionSchema.nullable(),
    /** Was der Beleg über seinen Aussteller sagt — auch ohne passenden Kreditor. */
    vendorIdentity: vendorIdentitySchema,
    /**
     * USt-IdNr. und IBAN treffen je genau einen aktiven Kreditor, aber auf
     * verschiedene — `null`, solange kein solcher Widerspruch vorliegt (auch
     * dann, wenn `vendorSuggestion` selbst `null` ist, weil gar keine oder
     * mehr als eine Kennung traf).
     */
    vendorConflict: vendorConflictSchema.nullable(),
})
    .strict();
// --- Die Dubletten --------------------------------------------------------
/** Ein offener Dublettenkandidat, aus der Sicht **eines** Belegs. */
export const duplicateCandidateSchema = z
    .object({
    linkId: uuid,
    invoiceId: uuid,
    invoiceNo: z.string().nullable(),
    vendorName: z.string(),
    otherInvoiceId: uuid,
    otherInvoiceNo: z.string().nullable(),
    otherStatus: incomingInvoiceStatusSchema,
    otherGrossTotal: centAmount,
    /** Welche Kriterien getroffen haben — Betrag, Datum, Nummer, Kreditor. */
    matchedCriteria: z.array(z.string()),
    detectedBy: z.string(),
})
    .strict();
export const duplicateCandidateListSchema = z
    .object({ candidates: z.array(duplicateCandidateSchema), total: z.number().int().nonnegative() })
    .strict();
// --- Die drei Berichte ----------------------------------------------------
/**
 * Eine Lückenmeldung — der einzige Kontrolltyp dieser Ära, der eine
 * **Auslassung** sieht.
 *
 * Jede andere Ansicht schaut auf etwas, das da ist. Diese sagt, von wem
 * **nichts** gekommen ist — und ein vergessener Portaldownload fällt sonst
 * niemandem auf.
 */
export const gapSchema = z
    .object({
    vendorId: uuid,
    creditorNo: z.string(),
    vendorName: z.string(),
    expectedCadence: expectedCadenceSchema,
    /**
     * Der Tag des letzten Eingangs — aus `received_at`, **nie aus dem
     * Hochladen**.
     *
     * Er ist nicht `null`: Ein Kreditor, von dem noch **nie** etwas kam, hat
     * keinen Rhythmus, an dem sich eine Überfälligkeit messen liesse, und
     * erscheint deshalb gar nicht in dieser Liste, sondern nur in der Zahl
     * `ohneJedenEingang` daneben.
     */
    letzterEingang: isoDate,
    faelligSeit: isoDate,
    tageUeberfaellig: z.number().int(),
})
    .strict();
/**
 * Der Lückenbericht.
 *
 * **`ohneErwartung` und `ohneJedenEingang` stehen daneben, weil die Zahl sonst
 * lügt** (Handbuch §3.132). „7 Lücken" heisst nicht „7 fehlende Rechnungen im
 * Betrieb": Kreditoren mit `irregular` sind **gar nicht** geprüft, und wer nie
 * geliefert hat, hat keinen Rhythmus, an dem man messen könnte.
 */
export const gapReportSchema = z
    .object({
    stichtag: isoDate,
    anzahl: z.number().int().nonnegative(),
    luecken: z.array(gapSchema),
    ohneErwartung: z.number().int().nonnegative(),
    ohneJedenEingang: z.number().int().nonnegative(),
})
    .strict();
/** Die Formklassen eines Ausschnitts — jeder Beleg in **genau einer**. */
export const formklassenSchema = z
    .object({
    belege: z.number().int().nonnegative(),
    mitXml: z.number().int().nonnegative(),
    mitTextlage: z.number().int().nonnegative(),
    ohneTextlage: z.number().int().nonnegative(),
    /**
     * Die Anteile — **Zeichenketten und keine Zahlen**.
     *
     * Sie sind auf ein Zehntelprozent gerundet und summieren sich durch die
     * grösste Restsumme auf genau `100.0`. Als `number` wäre die Zusicherung
     * beim ersten `toFixed` weg, und dieselbe Regel wie beim Geld gilt hier aus
     * demselben Grund: Die Zahl ist ein Ergebnis und keine Rechengrösse.
     */
    prozent: z
        .object({
        mitXml: z.string(),
        mitTextlage: z.string(),
        ohneTextlage: z.string(),
    })
        .strict(),
})
    .strict();
/** Die Erfassungszeiten eines Ausschnitts. */
export const erfassungszeitenSchema = z
    .object({
    /** Wie viele Belege überhaupt eine gemessene Dauer tragen. */
    gemessen: z.number().int().nonnegative(),
    median: z.number().nullable(),
    oberesQuartil: z.number().nullable(),
    prefillGemessen: z.number().int().nonnegative(),
    /** Der Mittelwert der stehengebliebenen Vorbelegungen — als Zeichenkette. */
    prefillKeptMittel: z.string().nullable(),
})
    .strict();
export const messzahlenSchema = z
    .object({ form: formklassenSchema, zeiten: erfassungszeitenSchema })
    .strict();
/**
 * Ein Urteil über eine Sprosse der Leiter — erreicht oder nicht, **mit Grund**.
 *
 * `sprosse: 4` ist keine Sprosse der Leiter, sondern das vierte Urteil in
 * derselben Liste: ob die je Kreditor gemerkte Vorbelegung trägt (Mittelwert
 * `prefill_kept` >= 4,0 von 6, Spec 8.3.1). Ihr Verfehlen begründet keinen
 * Parser, sondern eine Nacharbeit an der Vorbelegung selbst.
 */
export const sprossenurteilSchema = z
    .object({
    sprosse: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]),
    name: z.string(),
    erreicht: z.boolean(),
    begruendung: z.string(),
})
    .strict();
/**
 * Die Ausbeute EINER Formklasse — `[ER-sprossenmessung]`.
 *
 * `belege` ist NICHT dieselbe Zahl wie `Formklassen.mitXml` &c.: Ein
 * Archivstück ohne lesbare Bytes trägt eine Formklasse, aber keine Ausbeute.
 */
export const ausbeutezahlenSchema = z
    .object({
    belege: z.number().int().nonnegative(),
    /** Mittelwert, eine Nachkommastelle als Zeichenkette. `null` ohne Messung. */
    mittel: z.string().nullable(),
})
    .strict();
/**
 * Die Ausbeute der Auslese, gegliedert nach Formklasse — `[ER-sprossenmessung]`.
 *
 * Wie viele der Maskenfelder die Auslese je Formklasse im Schnitt GEFÜLLT
 * liefert. `ohneTextlage` ist das Argument, dessen Grösse über den Umfang von
 * Stufe 3 (echtes OCR) entscheidet — dort ist die Null mathematisch
 * garantiert, weil ohne XML und ohne Textlage keiner der zwei Leser etwas
 * findet.
 */
export const ausbeuteBerichtSchema = z
    .object({
    /** Wie viele Maskenfelder es überhaupt gibt — der Nenner von `mittel`. */
    vonFeldern: z.number().int().positive(),
    mitXml: ausbeutezahlenSchema,
    mitTextlage: ausbeutezahlenSchema,
    ohneTextlage: ausbeutezahlenSchema,
})
    .strict();
/**
 * Der Messbericht — die Zahl, ohne die „erst messen" ein frommer Wunsch bleibt.
 *
 * `von` und `bis` stehen im Bericht und nicht nur in der Anfrage: Eine
 * Formklassenverteilung ohne ihren Zeitraum ist eine Zahl ohne Bezugsgrösse.
 */
export const formatReportSchema = z
    .object({
    von: isoDate,
    bis: isoDate,
    gesamt: messzahlenSchema,
    jeHerkunft: z
        .object({
        email: messzahlenSchema,
        portal: messzahlenSchema,
        paper: messzahlenSchema,
    })
        .strict(),
    jeKreditorland: z.object({ de: messzahlenSchema, uebrige: messzahlenSchema }).strict(),
    // `.readonly()`: `MessberichtService.bericht()` liefert `readonly
    // Sprossenurteil[]`, und der Controller wandelt seine Antwort mit `as
    // FormatReport` um -- ein mutables Array hier liesse die Umwandlung an
    // exakt dieser Stelle scheitern (nachgemessen bei `[ER-sprossenmessung]`,
    // als das zusaetzliche Feld `ausbeute` den Typvergleich erstmals ueber
    // die Overlap-Schwelle von `tsc` trieb).
    sprossen: z.array(sprossenurteilSchema).readonly(),
    /** Nur auf Gesamtebene — nicht je Herkunft oder Land (Kopfkommentar `format.report.ts`). */
    ausbeute: ausbeuteBerichtSchema,
})
    .strict();
/** Ein Beleg mit § 14c-Verdacht. */
export const statedTaxSuspectSchema = z
    .object({
    invoiceId: uuid,
    vendorId: uuid,
    vendorName: z.string(),
    vendorCountryCode: z.string().length(2),
    invoiceNo: z.string().nullable(),
    invoiceDate: isoDate,
    currency: z.string().length(3),
    statedTaxTotal: centAmount,
    grossTotal: centAmount,
})
    .strict();
export const statedTaxSuspectListSchema = z
    .object({ suspects: z.array(statedTaxSuspectSchema), total: z.number().int().nonnegative() })
    .strict();
/**
 * Warum ein Beleg nicht gebucht ist.
 *
 * **Sichtbar statt still** (`E-261`). Stufe A bucht nicht; ohne diesen Bericht
 * wäre „nichts gebucht" eine Beobachtung, die niemand macht.
 */
export const nichtbuchungsgrundSchema = z.enum([
    'stufe_b_fehlt',
    'konto_nicht_benannt',
    'vor_zustaendigkeitsbeginn',
]);
export const unbookedInvoiceSchema = z
    .object({
    invoiceId: uuid,
    vendorId: uuid,
    vendorName: z.string(),
    invoiceNo: z.string().nullable(),
    invoiceDate: isoDate,
    currency: z.string().length(3),
    grossTotal: centAmount,
    expenseAccountNumber: z.string().nullable(),
    gruende: z.array(nichtbuchungsgrundSchema),
})
    .strict();
export const unbookedReportSchema = z
    .object({
    anzahl: z.number().int().nonnegative(),
    belege: z.array(unbookedInvoiceSchema),
    /**
     * Wie oft jeder Grund vorkommt.
     *
     * **Die Summe ist grösser als `anzahl`, und das ist kein Fehler:** Ein
     * Beleg kann mehrere Gründe zugleich haben. Wer die Zahlen addiert und mit
     * `anzahl` vergleicht, misst etwas anderes als er glaubt.
     */
    jeGrund: z.record(nichtbuchungsgrundSchema, z.number().int().nonnegative()),
})
    .strict();
// --- Die Anfrageformen ----------------------------------------------------
/**
 * Was beim Hochladen in der Abfragezeile steht.
 *
 * **Der Rumpf IST die Datei, die Angaben stehen daneben.** Dieselbe Bauform wie
 * beim Auszugsupload der Bankanbindung und aus demselben Grund: Ein
 * Multipart-Umschlag brächte eine Dateiablage, Zwischendateien und eine
 * Konfigurationsfläche mit, ohne eine Frage zu beantworten, die hier gestellt
 * wird. **Mehrere Dateien werden zu mehreren Anfragen**, und jede bekommt ihre
 * eigene Antwort — „gespeichert" oder „Mehrstück" —, statt in einem Sammelbericht
 * unterzugehen.
 */
export const incomingUploadRequestSchema = z
    .object({
    origin: incomingOriginSchema,
    kind: incomingDocumentKindSchema.default('invoice_file'),
    filename: z.string().trim().max(400).optional(),
    /** Der Medientyp, **wie empfangen**. Ohne Angabe der `content-type` der Anfrage. */
    mediaType: z.string().trim().min(1).max(200),
    creditorNo: z.string().trim().max(50).nullish(),
    vendorId: uuid.nullish(),
    invoiceDate: isoDate.nullish(),
    receivedAt: isoDate.nullish(),
})
    .strict();
/** Der Rumpf von `POST /incoming-invoices/:id/discard`. */
export const incomingDiscardRequestSchema = z
    .object({
    reason: incomingDiscardReasonSchema,
    note: z.string().trim().max(2000).nullish(),
})
    .strict();
/**
 * Der Rumpf von `POST /incoming-invoices/duplicates/:id/release`.
 *
 * **Der Grund ist Pflicht und keine Höflichkeit.** `[G]` GoBD Rz. 111 verlangt,
 * dass eine Veränderung samt ihres ursprünglichen Zustands erkennbar bleibt;
 * ein `released_at` ohne Grund sagt, **dass** jemand entschieden hat, und
 * verschweigt **warum**.
 */
export const duplicateReleaseRequestSchema = z
    .object({ reason: z.string().trim().min(3).max(2000) })
    .strict();
/**
 * Der Rumpf von `PATCH /incoming-invoices/:id`.
 *
 * Alle Felder optional — geändert wird, was dasteht. **Ein leerer Rumpf ist
 * keine Änderung und kein Fehler**: Er trifft keine Aussage, und eine
 * Ablehnung dafür machte aus einem folgenlosen Klick einen Vorgang.
 *
 * **`mailDocumentId` ist das einzige Feld, das keine Spalte an
 * `incoming_invoice` ist.** Es hängt ein bereits hochgeladenes Archivstück an
 * den Beleg (`incoming_document.invoice_id`) — der Weg für die Mail, die erst
 * nach der Erfassung auftaucht. Ein `null` ist deshalb **keine Ablösung**: Die
 * Zuordnung ist seit `0047` einmalig und lässt sich nicht zurücknehmen; `null`
 * heisst „dazu nichts gesagt".
 *
 * **`beschreibung` kam am 2026-08-27 dazu, additiv** — sie ist keines der zehn
 * Erfassungsfelder (`NORTHSTAR.md`), sondern eine rein organisatorische Notiz
 * (`0089_eingangsbeleg_beschreibung.sql`). `null` löscht den Text, ein
 * fehlender Schlüssel lässt ihn stehen — dieselbe Regel wie bei jedem anderen
 * nullbaren Feld hier.
 */
export const incomingPatchRequestSchema = z
    .object({
    mailDocumentId: uuid.nullish(),
    invoiceNo: z.string().trim().min(1).max(200).nullish(),
    invoiceDate: isoDate.optional(),
    serviceDate: isoDate.nullish(),
    serviceMonth: isoDate.nullish(),
    receivedAt: isoDate.optional(),
    expenseAccountNumber: z.string().trim().min(1).max(20).nullish(),
    dueDate: isoDate.nullish(),
    netTotal: z.string().optional(),
    taxTotal: z.string().optional(),
    grossTotal: z.string().optional(),
    beschreibung: z.string().trim().max(2000).nullish(),
})
    .strict();
//# sourceMappingURL=eingangsrechnungen.js.map